import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "../../../services/authService";
import { setCredentials } from "../store/authSlice";
import apiClient from "../../../core/config/api";
import "./r_sign_in.css";
import "./r_otp_verify.css";

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_EXPIRY_MINUTES = 10;

const R_RegistrationOtpVerify: React.FC = () => {
    const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(""));
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);
    const [otpExpired, setOtpExpired] = useState(false);

    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const email = useMemo(
        () => (location.state as { email?: string; plan_name?: string } | null)?.email || "",
        [location.state]
    );
    const planName = useMemo(
        () => (location.state as { email?: string; plan_name?: string } | null)?.plan_name || "",
        [location.state]
    );

    // Redirect back to signup if no email in state
    useEffect(() => {
        if (!email) {
            navigate("/signup/30-day-free-trial", { replace: true });
        }
    }, [email, navigate]);

    // Resend cooldown countdown
    useEffect(() => {
        if (timer <= 0) return;
        const id = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(id);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [timer]);

    const otpValue = otpDigits.join("");
    const isSubmitDisabled = loading || otpValue.length !== 6 || otpExpired;
    const canResend = !resendLoading && !loading && timer === 0;

    const handleOtpChange = (value: string, index: number) => {
        const digit = value.replace(/\D+/g, "").slice(0, 1);
        setOtpDigits((prev) => {
            const next = [...prev];
            next[index] = digit;
            return next;
        });
        if (digit && otpRefs.current[index + 1]) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < otpDigits.length - 1) {
            e.preventDefault();
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D+/g, "").slice(0, 6);
        if (!pasted) return;
        const next = Array(6).fill("");
        pasted.split("").forEach((char, idx) => { next[idx] = char; });
        setOtpDigits(next);
        const lastIndex = Math.min(pasted.length, 6) - 1;
        if (lastIndex >= 0 && otpRefs.current[lastIndex]) {
            otpRefs.current[lastIndex]?.focus();
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isSubmitDisabled) return;
        setError(null);
        setSuccessMessage(null);

        try {
            setLoading(true);
            const res = await authService.otpVerify({
                email,
                purpose: "registration",
                otp_code: otpValue,
                plan: planName || undefined,
            });

            // If the backend returned an access token, auto-login and redirect to dashboard
            if (res?.access_token) {
                dispatch(setCredentials({
                    access_token: res.access_token,
                    token_type: res.token_type || "bearer",
                    expires_in: res.expires_in || 0,
                    id: res.id!,
                    role_id: res.role_id!,
                    role_name: res.role_name,
                    permissions: res.permissions || [],
                    email: res.email!,
                    first_name: res.first_name!,
                    last_name: res.last_name,
                }));
                const isPaidPlan = planName && planName.toLowerCase() !== "30-day-free-trial";
                setSuccessMessage(
                    isPaidPlan
                        ? "Email verified successfully. Redirecting to payment..."
                        : "Email verified successfully. Redirecting to dashboard..."
                );

                if (planName) {
                    // Pay As You Go signup: straight to the Stripe checkout for the
                    // amount the signup link stands for. Every refusal is recorded in
                    // the audit log by the server; here the customer gets a clear
                    // message and lands in the app, where they can buy credits later.
                    if (planName.toLowerCase().startsWith("pay-as-you-go")) {
                        try {
                            const { data: plan } = await apiClient.get("/pricing/plans/validate", {
                                params: { slug: planName },
                            });
                            const credits = Number(plan?.payg?.credits || 0);
                            if (!credits) throw new Error("This Pay As You Go link is no longer valid.");
                            const { data } = await apiClient.post("/stripe-payment/create-topup-checkout-session", {
                                credits,
                                is_new_signup: true,
                            });
                            if (!data?.checkout_url) throw new Error("Could not start the payment.");
                            window.location.href = data.checkout_url;
                        } catch (payErr: any) {
                            const detail = payErr?.response?.data?.detail || payErr?.response?.data?.message || payErr?.message;
                            setSuccessMessage(null);
                            setError(
                                `Your account is ready, but we couldn't start the payment${detail ? `: ${detail}` : "."} ` +
                                "You can buy credits any time from Credits & Billing."
                            );
                            setTimeout(() => navigate("/credit-billing", { replace: true }), 4000);
                        }
                        return;
                    }

                    // Free trial signup — credits already assigned during registration, go to dashboard
                    if (planName.toLowerCase() === "30-day-free-trial") {
                        setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
                        return;
                    }

                    try {
                        const plansRes = await apiClient.get("/pricing/plans", {
                            params: { is_active: true },
                            headers: { Authorization: `Bearer ${res.access_token}` },
                        });
                        const plans: any[] = Array.isArray(plansRes.data) ? plansRes.data : [];
                        console.log("[PlanRedirect] planName from state:", planName);
                        console.log("[PlanRedirect] plans from API:", plans.map((p: any) => ({ name: p.name, billing_interval: p.billing_interval })));

                        // Split "pro-monthly" → name: "pro", interval: "monthly"
                        const INTERVAL_SUFFIXES = ["-one-time", "-monthly", "-yearly", "-annual"];
                        const lowerParam = planName.toLowerCase();
                        let planNamePart = planName;
                        let intervalPart: string | null = null;
                        for (const suffix of INTERVAL_SUFFIXES) {
                            if (lowerParam.endsWith(suffix)) {
                                planNamePart = planName.slice(0, -suffix.length);
                                intervalPart = suffix.slice(1).replace(/-/g, "_"); // -one-time → one_time, -monthly → monthly
                                break;
                            }
                        }
                        console.log("[PlanRedirect] split → name:", planNamePart, "| interval:", intervalPart);

                        const normalize = (s: string) => s.toLowerCase().replace(/[-_]/g, " ").trim();
                        const match = plans.find((p: any) => {
                            const nameMatches = normalize(p.name || "") === normalize(planNamePart);
                            const intervalMatches = !intervalPart || p.billing_interval?.toLowerCase() === intervalPart;
                            return nameMatches && intervalMatches;
                        });
                        console.log("[PlanRedirect] match found:", match ? match.name : "NONE");
                        if (match) {
                            setTimeout(() => navigate("/payment-details", {
                                replace: true,
                                state: {
                                    planId: match.id,
                                    planName: match.name,
                                    description: match.description ?? null,
                                    price: Number(match.price) || 0,
                                    currency: match.currency,
                                    interval: match.billing_interval,
                                    base_credits: Number(match.base_credits) || 0,
                                    credit_notes: match.credit_notes ?? null,
                                    features: match.features ?? null,
                                    isNewSignup: true,
                                },
                            }), 1500);
                            return;
                        }
                    } catch (planErr: any) {
                        console.error("[PlanRedirect] plan lookup failed:", planErr?.response?.status, planErr?.message);
                    }
                }

                setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
            } else {
                // Fallback: no token returned, redirect to login
                setSuccessMessage(res?.message || "Email verified successfully. You can now sign in.");
                setTimeout(() => navigate("/login", { replace: true }), 2000);
            }
        } catch (err: any) {
            const msg: string = err?.message || "Failed to verify OTP";
            if (msg.toLowerCase().includes("expired")) {
                setOtpExpired(true);
                setError("OTP expired, please try again");
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;
        setError(null);
        setSuccessMessage(null);
        setOtpExpired(false);
        setOtpDigits(Array(6).fill(""));

        try {
            setResendLoading(true);
            await authService.otpRequest({ email, purpose: "registration" });
            setSuccessMessage("A new verification code has been sent to your email.");
            setTimer(RESEND_COOLDOWN_SECONDS);
        } catch (err: any) {
            setError(err?.message || "Failed to resend verification code.");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="keep-box-in-center">
                <img src="/images/right-emails-logo.svg" className="form-logo" alt="Right Emails" />

                <div className="form-box otp-verify-box">
                    <p className="form-header">Verify Your Email</p>
                    <p className="form-inform">
                        We sent a {OTP_EXPIRY_MINUTES}-minute verification code to
                    </p>
                    <p className="otp-email-display">{email}</p>

                    {error && <p className="error-text">{error}</p>}
                    {successMessage && <p className="success-text">{successMessage}</p>}

                    <form onSubmit={handleSubmit}>
                        <div className="tch-section">
                            <div className="otp-inputs-row" onPaste={handleOtpPaste}>
                                {otpDigits.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={(el) => { otpRefs.current[idx] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="\d*"
                                        maxLength={1}
                                        className={`r-form-email otp-digit-input${otpExpired ? " otp-digit-expired" : ""}`}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(e.target.value, idx)}
                                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                        disabled={loading}
                                        autoFocus={idx === 0}
                                    />
                                ))}
                            </div>
                        </div>

                        <button
                            type="submit"
                            className={`form-submit${isSubmitDisabled ? " disabled" : ""}`}
                            disabled={isSubmitDisabled}
                        >
                            {loading ? "Verifying..." : "Verify Email"}
                        </button>
                    </form>

                    <div className="otp-resend-row">
                        {timer > 0 ? (
                            <span className="otp-resend-timer">
                                Resend code in <strong>{timer}s</strong>
                            </span>
                        ) : (
                            <span
                                className={`ask-sign-up${!canResend ? " otp-resend-disabled" : ""}`}
                                onClick={handleResend}
                            >
                                {resendLoading ? "Sending..." : "Resend verification code"}
                            </span>
                        )}
                    </div>

                    <p className="asking">
                        Wrong email?{" "}
                        <span
                            className="ask-sign-up"
                            onClick={() => navigate(`/signup/${encodeURIComponent(planName || "30-day-free-trial")}`)}
                        >
                            Go back
                        </span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default R_RegistrationOtpVerify;
